import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { universitiesAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Building,
  MapPin,
  Globe,
  Award,
  Calendar,
  GraduationCap,
  DollarSign,
  Clock,
  BookOpen,
  Users,
  ExternalLink,
  ArrowLeft,
  Loader2,
  CheckCircle,
  FileText
} from 'lucide-react';

const UniversityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchUniversity();
  }, [id]);

  const fetchUniversity = async () => {
    try {
      const res = await universitiesAPI.getById(id);
      setUniversity(res.data);
    } catch (error) {
      console.error('Error fetching university:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency, country) => {
    const countryCurrencyMap = {
      'UK': 'GBP', 'United Kingdom': 'GBP',
      'USA': 'USD', 'United States': 'USD',
      'Canada': 'CAD', 'Australia': 'AUD',
      'Germany': 'EUR', 'France': 'EUR', 'Netherlands': 'EUR', 'Ireland': 'EUR',
      'New Zealand': 'NZD'
    };
    const currencyCode = currency || countryCurrencyMap[country] || 'GBP';
    const locale = currencyCode === 'GBP' ? 'en-GB' : currencyCode === 'EUR' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!university) {
    return (
      <div className="min-h-screen bg-[#f8f9fa]">
        <Navbar />
        <main className="pt-20 pb-12">
          <div className="max-w-4xl mx-auto px-4 py-20 text-center">
            <Building className="w-16 h-16 text-gray-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">University Not Found</h1>
            <p className="text-gray-500 mb-6">The university you&apos;re looking for doesn&apos;t exist.</p>
            <Button onClick={() => navigate('/courses')} className="bg-teal-600 text-white hover:bg-teal-700">
              Browse Courses
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const courses = university.courses || [];
  const stats = university.stats || {};

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-100 dark:from-slate-800 via-[#e9ecef] dark:via-slate-800 to-[#f1f3f5] dark:to-slate-900 py-12 transition-colors">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* University Logo */}
              <div className="w-24 h-24 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                {university.logo_url ? (
                  <img src={university.logo_url} alt={university.name} className="w-20 h-20 object-contain" />
                ) : (
                  <Building className="w-12 h-12 text-teal-600 dark:text-teal-400" />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {university.ranking && (
                    <Badge className="bg-teal-600/20 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400">
                      <Award className="w-3 h-3 mr-1" />
                      Rank #{university.ranking}
                    </Badge>
                  )}
                  <Badge variant="outline" className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300">
                    {university.country}
                  </Badge>
                </div>
                
                <h1 className="text-3xl md:text-4xl font-semibold text-gray-800 dark:text-white mb-2">{university.name}</h1>
                
                <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-300">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {university.city}, {university.country}
                  </span>
                  {university.established && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Est. {university.established}
                    </span>
                  )}
                  {university.website && (
                    <a 
                      href={university.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                    >
                      <Globe className="w-4 h-4" />
                      Website
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-sm">Courses</span>
                </div>
                <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.total_courses || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <span className="text-sm">Avg. Tuition</span>
                </div>
                <p className="text-2xl font-semibold text-teal-600 dark:text-teal-400">{formatCurrency(stats.avg_tuition, null, university?.country)}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <GraduationCap className="w-4 h-4" />
                  <span className="text-sm">Degree Levels</span>
                </div>
                <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.degree_levels?.length || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">Fields of Study</span>
                </div>
                <p className="text-2xl font-semibold text-gray-800 dark:text-white">{stats.fields_of_study?.length || 0}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-1 mb-8">
              <TabsTrigger value="overview" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white dark:text-gray-300">
                Overview
              </TabsTrigger>
              <TabsTrigger value="admission" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white dark:text-gray-300">
                Admission Criteria
              </TabsTrigger>
              <TabsTrigger value="courses" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white dark:text-gray-300">
                Courses ({courses.length})
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                  {/* About */}
                  <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">About {university.name}</h2>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {university.description || `${university.name} is a prestigious institution located in ${university.city}, ${university.country}. The university offers a wide range of undergraduate and postgraduate programs across various disciplines.`}
                    </p>
                  </div>

                  {/* Facilities */}
                  {university.facilities?.length > 0 && (
                    <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Facilities</h2>
                      <div className="flex flex-wrap gap-2">
                        {university.facilities.map((facility, idx) => (
                          <Badge key={`${`+var_name+`}-${idx}`} variant="outline" className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300">
                            <CheckCircle className="w-3 h-3 mr-1 text-green-600 dark:text-green-400" />
                            {facility}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Popular Courses */}
                  {university.popular_courses?.length > 0 && (
                    <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Popular Programs</h2>
                      <div className="space-y-2">
                        {university.popular_courses.map((course, idx) => (
                          <div key={`${`+var_name+`}-${idx}`} className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                            <GraduationCap className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                            {course}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                  <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Quick Facts</h3>
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Location</span>
                        <span className="text-gray-800 dark:text-white">{university.city}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Country</span>
                        <span className="text-gray-800 dark:text-white">{university.country}</span>
                      </div>
                      {university.ranking && (
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Ranking</span>
                          <span className="text-teal-600">#{university.ranking}</span>
                        </div>
                      )}
                      {university.established && (
                        <div className="flex justify-between">
                          <span className="text-gray-500 dark:text-gray-400">Established</span>
                          <span className="text-gray-800 dark:text-white">{university.established}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Ready to Apply?</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                      Start your application journey with H E Direct today.
                    </p>
                    <Link to={isAuthenticated ? "/courses" : "/register"}>
                      <Button className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold">
                        {isAuthenticated ? "Browse Courses" : "Get Started"}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Admission Criteria Tab */}
            <TabsContent value="admission">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pakistan Students */}
                <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                      <span className="text-lg">🇵🇰</span>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">For Pakistani Students</h2>
                  </div>
                  
                  {university.pakistan_requirements ? (
                    <div className="space-y-4 text-gray-600 dark:text-gray-300" data-testid="pak-reqs-detail">
                      {university.pakistan_requirements.summary && (
                        <p className="text-sm text-gray-700 dark:text-gray-200 italic">
                          {university.pakistan_requirements.summary}
                        </p>
                      )}
                      {university.pakistan_requirements.academic && (
                        <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                          <h4 className="font-medium text-gray-800 dark:text-white mb-2">Academic</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {university.pakistan_requirements.academic.undergraduate_min && (
                              <li><span className="font-medium">Undergraduate:</span> {university.pakistan_requirements.academic.undergraduate_min}</li>
                            )}
                            {university.pakistan_requirements.academic.postgraduate_min && (
                              <li><span className="font-medium">Postgraduate:</span> {university.pakistan_requirements.academic.postgraduate_min}</li>
                            )}
                            {university.pakistan_requirements.academic.phd_min && (
                              <li><span className="font-medium">PhD:</span> {university.pakistan_requirements.academic.phd_min}</li>
                            )}
                            {!university.pakistan_requirements.academic.undergraduate_min && !university.pakistan_requirements.academic.postgraduate_min && (
                              <li className="text-gray-500 italic">Program-specific — check with admissions team</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {university.pakistan_requirements.english && (
                        <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                          <h4 className="font-medium text-gray-800 dark:text-white mb-2">English Proficiency</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {university.pakistan_requirements.english.ielts && (
                              <li>IELTS overall <span className="font-mono">{university.pakistan_requirements.english.ielts}</span>{university.pakistan_requirements.english.ielts_subscore_min ? <span> (min subscore <span className="font-mono">{university.pakistan_requirements.english.ielts_subscore_min}</span>)</span> : null}</li>
                            )}
                            {university.pakistan_requirements.english.toefl_ibt && (
                              <li>TOEFL iBT <span className="font-mono">{university.pakistan_requirements.english.toefl_ibt}+</span></li>
                            )}
                            {university.pakistan_requirements.english.pte && (
                              <li>PTE <span className="font-mono">{university.pakistan_requirements.english.pte}+</span></li>
                            )}
                            {university.pakistan_requirements.english.duolingo && (
                              <li>Duolingo <span className="font-mono">{university.pakistan_requirements.english.duolingo}+</span></li>
                            )}
                            {university.pakistan_requirements.english.alternatives && (
                              <li className="italic">{university.pakistan_requirements.english.alternatives}</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {university.pakistan_requirements.financial && (
                        <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                          <h4 className="font-medium text-gray-800 dark:text-white mb-2">Financial Proof</h4>
                          <p className="text-sm">{university.pakistan_requirements.financial}</p>
                        </div>
                      )}
                      {university.pakistan_requirements.documents && university.pakistan_requirements.documents.length > 0 && (
                        <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                          <h4 className="font-medium text-gray-800 dark:text-white mb-2">Required Documents</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {university.pakistan_requirements.documents.map((doc, i) => (
                              <li key={i}>{doc}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {university.pakistan_requirements.visa && (
                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                          <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Visa</h4>
                          <ul className="list-disc list-inside space-y-1 text-sm text-amber-900 dark:text-amber-100">
                            {university.pakistan_requirements.visa.type && <li><span className="font-medium">Type:</span> {university.pakistan_requirements.visa.type}</li>}
                            {university.pakistan_requirements.visa.processing_weeks && <li><span className="font-medium">Processing:</span> {university.pakistan_requirements.visa.processing_weeks} weeks</li>}
                            {university.pakistan_requirements.visa.common_blockers && <li className="text-amber-800 dark:text-amber-200"><span className="font-medium">Common blockers:</span> {university.pakistan_requirements.visa.common_blockers}</li>}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : university.admission_criteria_pakistan ? (
                    <div className="prose prose-invert max-w-none">
                      <div className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
                        {university.admission_criteria_pakistan}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 text-gray-600 dark:text-gray-300">
                      <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                        <h4 className="font-medium text-gray-800 dark:text-white mb-2">Undergraduate Programs</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>FSc/A-Levels with minimum 60% marks</li>
                          <li>IELTS 6.0 overall (no band less than 5.5) or PTE 50+</li>
                          <li>Valid passport</li>
                          <li>Statement of Purpose</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                        <h4 className="font-medium text-gray-800 dark:text-white mb-2">Postgraduate Programs</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>Bachelor&apos;s degree with minimum 55% marks (2:2 equivalent)</li>
                          <li>IELTS 6.5 overall (no band less than 6.0) or PTE 58+</li>
                          <li>Two academic references</li>
                          <li>CV/Resume</li>
                          <li>Statement of Purpose</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {/* International Students */}
                <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">For International Students</h2>
                  </div>
                  
                  {university.admission_criteria_international ? (
                    <div className="prose prose-invert max-w-none">
                      <div className="text-gray-600 whitespace-pre-line">
                        {university.admission_criteria_international}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 text-gray-600 dark:text-gray-300">
                      <div className="p-4 bg-[#e9ecef]/50 rounded-lg">
                        <h4 className="font-medium text-gray-800 dark:text-white mb-2">Undergraduate Programs</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>High school diploma or equivalent qualification</li>
                          <li>IELTS 6.0+ or TOEFL 80+ or PTE 50+</li>
                          <li>Valid passport</li>
                          <li>Financial proof for visa</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-100 dark:bg-slate-700 rounded-lg">
                        <h4 className="font-medium text-gray-800 dark:text-white mb-2">Postgraduate Programs</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm">
                          <li>Bachelor&apos;s degree from recognized institution</li>
                          <li>IELTS 6.5+ or TOEFL 90+ or PTE 58+</li>
                          <li>Academic transcripts</li>
                          <li>Letters of recommendation</li>
                          <li>Work experience (for some programs)</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mt-6 transition-colors">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Need Help with Admission?</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Our expert counselors can guide you through the admission process and help you prepare a strong application.
                </p>
                <div className="flex gap-4">
                  <Link to="/check-eligibility">
                    <Button variant="outline" className="border-teal-400 text-teal-600 dark:text-teal-400 hover:bg-teal-600/10">
                      Check Eligibility
                    </Button>
                  </Link>
                  <Link to="/help">
                    <Button className="bg-teal-600 text-white hover:bg-teal-700">
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </div>
            </TabsContent>

            {/* Courses Tab */}
            <TabsContent value="courses">
              {courses.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses.map((course) => (
                    <div key={course.id} className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-5 hover:border-teal-300 dark:hover:border-teal-500 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <Badge variant="outline" className="border-teal-400 text-teal-600 dark:text-teal-400">
                          {course.degree_level}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">{course.name}</h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">{course.field_of_study}</p>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {course.duration_months} months
                        </span>
                        <span className="text-teal-600 dark:text-teal-400 font-medium">
                          {formatCurrency(course.tuition_fee, course.currency, course.country || university?.country)}
                        </span>
                      </div>
                      
                      <Link to={isAuthenticated ? `/courses` : '/register'}>
                        <Button className="w-full bg-teal-600/10 text-teal-600 hover:bg-teal-600 hover:text-white">
                          {isAuthenticated ? 'Apply Now' : 'Register to Apply'}
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-gray-500 dark:text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">No Courses Listed</h3>
                  <p className="text-gray-500 dark:text-gray-400">Course information will be updated soon.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UniversityDetailPage;
